import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Lock, Trash2, AlertTriangle, Loader2, CheckCircle } from "lucide-react";
import { API, useAuth } from "../App";

export default function Settings() {
  const { user, logout } = useAuth();
  const [fullName, setFullName] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete all transactions state
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearingData, setClearingData] = useState(false);

  // Clean up receipt duplicates state
  const [showReceiptsConfirm, setShowReceiptsConfirm] = useState(false);
  const [cleaningReceipts, setCleaningReceipts] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (user?.full_name) setFullName(user.full_name);
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/settings`, { full_name: fullName });
      toast.success("Profile saved");
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCleanupReceipts = async () => {
    setCleaningReceipts(true);
    try {
      const res = await axios.delete(`${API}/api/transactions/bulk-delete-receipts`);
      toast.success(`Deleted ${res.data.deleted} duplicate receipt entries.`);
      setShowReceiptsConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clean up receipts");
    } finally {
      setCleaningReceipts(false);
    }
  };

  const handleClearTransactions = async () => {
    setClearingData(true);
    try {
      await axios.delete(`${API}/api/transactions/all/clear`);
      toast.success("All transactions deleted. You can re-upload your statements now.");
      setShowClearConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear transactions");
    } finally {
      setClearingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/api/account`);
      toast.success("Account deleted");
      logout();
    } catch {
      toast.error("Failed to delete account");
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your account and data</p>
      </div>

      {/* Profile */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <User size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Full name</label>
          <input
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primary transition"
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
          <input
            value={user?.email || ""}
            disabled
            className="w-full px-3 py-2.5 border border-gray-100 rounded-lg text-sm text-gray-400 bg-gray-50"
          />
        </div>
        <button
          onClick={handleSaveProfile}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary-dark transition disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          Save profile
        </button>
      </div>

      {/* Security */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-gray-900">Security</h2>
        </div>
        <p className="text-xs text-gray-500">
          To change your password, log out and use the "Forgot password" option on the login screen.
        </p>
      </div>

      {/* Danger Zone */}
      <div className="bg-white border border-red-200 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={16} className="text-red-500" />
          <h2 className="text-sm font-semibold text-red-700">Danger Zone</h2>
        </div>

        {/* ── Clean up receipt duplicates ── */}
        <div className="border border-red-100 rounded-xl p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Clean up duplicate receipt entries</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Removes old-format "Visa Purchase - Receipt XXXXXX" entries that have no merchant name (duplicates from ING re-upload).
              </p>
            </div>
            {!showReceiptsConfirm && (
              <button
                onClick={() => setShowReceiptsConfirm(true)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition"
              >
                <Trash2 size={14} /> Clean up
              </button>
            )}
          </div>

          {showReceiptsConfirm && (
            <div className="space-y-3">
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-red-900 mb-1">⚠ This will delete receipt-only duplicate entries</p>
                <p className="text-xs text-red-700">Only entries matching "Visa Purchase - Receipt XXXXXX" with no merchant name are removed. Your real transactions with merchant names are kept.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReceiptsConfirm(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCleanupReceipts}
                  disabled={cleaningReceipts}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {cleaningReceipts ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Yes, clean up receipts
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Clear all transactions ── */}
        <div className="border border-red-100 rounded-xl p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Delete all transactions</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Removes all imported transactions so you can start fresh. Your account stays active.
              </p>
            </div>
            {!showClearConfirm && (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition"
              >
                <Trash2 size={14} /> Clear data
              </button>
            )}
          </div>

          {showClearConfirm && (
            <div className="space-y-3">
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-red-900 mb-1">⚠ This will permanently delete:</p>
                <ul className="text-xs text-red-700 space-y-0.5">
                  <li>• All imported transaction records</li>
                  <li>• All uploaded statement history</li>
                </ul>
                <p className="text-xs text-red-700 mt-2">Your account, subscription, and login are kept. You can re-upload statements after this.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearTransactions}
                  disabled={clearingData}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {clearingData ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Yes, delete all transactions
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Delete account ── */}
        <div className="border border-red-100 rounded-xl p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Delete account</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Permanently deletes your account and all data. This cannot be undone.
              </p>
            </div>
            {!showDeleteConfirm && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition"
              >
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>

          {showDeleteConfirm && (
            <div className="space-y-3">
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-red-900 mb-1">⚠ This will permanently delete:</p>
                <ul className="text-xs text-red-700 space-y-0.5">
                  <li>• All your transaction records</li>
                  <li>• Your account profile and settings</li>
                  <li>• Access to any active subscription</li>
                </ul>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <strong>DELETE</strong> to confirm
                </label>
                <input
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="w-full px-3 py-2.5 border border-red-300 rounded-lg text-sm outline-none focus:border-red-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                  className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== "DELETE" || deletingAccount}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {deletingAccount ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
